import { type EditorValue, LiteEditor } from '@octopus-do/octopus-editor'
import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useSelector } from 'react-redux'

import { useContentAttach } from '@/components/App/Modal/BlockModal'
import { noop } from '@/helpers'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { Recipient } from '@/types'
import { isSubmitHotkey } from '@/utils'
import { buildMention } from '../../utils'
import css from './styles.module.css'

interface Props {
  error: boolean
  loading: boolean
  readonly: boolean
  onCancel: () => void
  onSubmit: () => void
  value: EditorValue
  onUpdate: (value: EditorValue) => void
  submitButtonText: string
}

export const Textarea: React.FC<Props> = ({
  error,
  loading,
  onCancel,
  onSubmit,
  onUpdate,
  readonly,
  submitButtonText,
  value,
}) => {
  const { onUpload } = useContentAttach()
  const members: Recipient[] = useSelector(
    migrationProjectSelector.getProjectRecipients
  )

  // the artboard is off: no writing or sending a comment, new or edited
  const artboardDisabled = useArtBoardDisabled()
  const locked = readonly || artboardDisabled

  // the editor has no onKeyDown of its own, the keydown bubbles up to here
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isSubmitHotkey(event) || locked || loading) {
      return
    }

    event.preventDefault()
    onSubmit()
  }

  const mentions = members.map(buildMention)

  return (
    <div className={css.wrapper} onKeyDown={onKeyDown}>
      <div className={css.body} onMouseDown={noop} onBlur={noop}>
        <LiteEditor
          mentions={mentions}
          value={value}
          onFilesUpload={onUpload}
          onChange={onUpdate}
          readonly={artboardDisabled}
          autofocus
        />
      </div>

      {error && (
        <div className={css.error}>Something went wrong. Please retry.</div>
      )}

      <div className={css.footer}>
        <div className={css.buttons}>
          <Button
            size="medium"
            label={submitButtonText}
            loading={loading}
            disabled={locked}
            onClick={onSubmit}
          />

          <Button
            priority="secondary"
            size="medium"
            label="Cancel"
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  )
}
